import { Alert, Button, Input, Table } from 'antd';
import { EditorState } from 'draft-js';
import { Formik } from 'formik';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import RichTextEditor, {
  editorStateToHtml,
} from '../../../foundation/components/rich_text_editor/RichTextEditor';
import { MUTED_TEXT } from '../../../foundation/config/colors';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { VariantSample } from '../redux';
import { sampleEditFormValidationSchema } from '../validation_schemas/sample_edit_validation_schema';
import { useSampleEditModalLogic } from './hooks';

type SampleModalType = {
  closeModal: () => void;
  sample?: VariantSample;
  handleSubjectModalOpen: () => void;
  handleSubjectModalClose: () => void;
};

const SampleEditModal = ({
  closeModal,
  sample,
  handleSubjectModalOpen,
}: SampleModalType) => {
  const lock = useSelector(selectVariantLock);
  const { canInteract, canEditWorkflow, isReadOnly } = useVariantAccess();

  // All logic extracted to custom hook for separation of concerns
  const {
    isLoading,
    variantTableColumns,
    initialEditorState,
    initialIndicationEditorState,
    initialValues,
    modalOpts,
    getIsSaveDisabled,
    getIsUnlinkDisabled,
    handleFormSubmit,
    handleUnlinkSample,
  } = useSampleEditModalLogic({ closeModal, sample });

  // Keep separate states for the editors
  const [commentEditorState, setCommentEditorState] =
    useState<EditorState>(initialEditorState);
  const [indicationEditorState, setIndicationEditorState] =
    useState<EditorState>(initialIndicationEditorState);

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={sampleEditFormValidationSchema}
        enableReinitialize
      >
        {({
          errors,
          values,
          handleSubmit,
          handleBlur,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          // Centralized disable logic via getIsSaveDisabled
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Sample ID:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="sample_external_id"
                    type="text"
                    value={values.sample_external_id}
                    disabled
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Subject ID:</div>
                <div className="c-form-field__wrapper">
                  <a
                    href="#"
                    rel="noreferrer"
                    className="input-like-link"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubjectModalOpen();
                    }}
                  >
                    {values.subject_external_id}
                  </a>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Indication:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    name="indication"
                    editorState={indicationEditorState}
                    onChange={(newState) => {
                      setIndicationEditorState(newState);
                      const htmlContent = editorStateToHtml(newState);
                      setFieldValue('indication', htmlContent);
                    }}
                    onBlur={() => handleBlur('indication')}
                    placeholder="Enter indication"
                    label=""
                    readOnly={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.indication ? errors.indication : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Variants:</div>
                <div className="c-form-field__wrapper">
                  {sample?.genotype_variants?.length ? (
                    <Table
                      dataSource={sample.genotype_variants}
                      pagination={false}
                      showHeader={false}
                      bordered={false}
                      size="small"
                      columns={variantTableColumns}
                    />
                  ) : (
                    <div style={{ color: MUTED_TEXT, fontStyle: 'italic' }}>
                      No variants found for this sample
                    </div>
                  )}
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Comments:</div>
                <div className="c-form-field__wrapper">
                  <RichTextEditor
                    name="comment"
                    editorState={commentEditorState}
                    onChange={(newState) => {
                      setCommentEditorState(newState);
                      const htmlContent = editorStateToHtml(newState);
                      setFieldValue('comment', htmlContent);
                    }}
                    onBlur={() => handleBlur('comment')}
                    placeholder=""
                    label=""
                    readOnly={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {typeof errors.comment === 'string'
                      ? errors.comment
                      : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  danger
                  type="primary"
                  className="h-proband-modal__form-unlink-btn"
                  onClick={handleUnlinkSample}
                  style={{ marginRight: 'auto' }}
                  disabled={getIsUnlinkDisabled() || !canEditWorkflow}
                >
                  Delete sample from variant
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default SampleEditModal;
